import Ky, { Options } from 'ky';
import { GQLPlugin, GQLVariables, GQLBlob, GQLBeforeRequestHook, GQLAfterResponseHook, GQLOnClientErrorHook, GQLNormalizedOptions } from './GQLTypes';
import { getResult } from './helpers';
import { ClientError } from './ClientError';

class GQLClient {
  client: typeof Ky | null = null;
  beforeRequest: GQLBeforeRequestHook[] = [];
  afterResponse: GQLAfterResponseHook[] = [];
  onClientError: GQLOnClientErrorHook[] = [];

  constructor (url: string, ky: typeof Ky, defaultOptions?: Options) {
    this.client = ky.create({
      prefixUrl: `https://${url}`,
      method: 'post',
      mode: 'cors',
      headers: new Headers([
        ['X-Requested-With', 'XMLHttpRequest'],
        ['Content-Type', 'application/json'],
      ]),
      ...defaultOptions,
    });
  }

  private async _request (query: string, options: Options, variables?: GQLVariables) {
    const extendedClient = this.client?.extend({
      hooks: {
        beforeRequest: this.beforeRequest.map(hook => (request, options) => {
          const newRequest = hook(request, options);
          if (newRequest) {
            return newRequest;
          }
        }),
        afterResponse: this.afterResponse.map(hook => (request, options, response) => {
          hook(request, options, response);
        }),
      },
    });

    if (extendedClient) {
      try {
        const response = await extendedClient('graphql', {
          throwHttpErrors: false,
          ...options,
        });

        const result = await getResult(response);
        if (
          response.ok &&
          result &&
          typeof result !== 'string' &&
          !result.errors &&
          result.data
        ) {
          return result.data;
        } else {
          const errorResult = (typeof result === 'string') ? { error: result } : result;
          throw new ClientError(
            { ...errorResult, status: response.status },
            { query, variables, options },
          );
        }
      } catch (error) {
        let exception: Error = error;
        let throwError = true;
        for (const clientErrorPlugin of this.onClientError) {
          try {
            throwError = true;
            clientErrorPlugin(exception, options as GQLNormalizedOptions);
            throwError = false;
          } catch (err) {
            exception = err;
          }
        }
        if (throwError) throw exception;
      }
    } else {
      throw new Error('You need to initialize the GraphQL client before using it.');
    }
  }

  public async request (query: string, variables?: GQLVariables, options?: GQLNormalizedOptions | Options) {
    return this._request(
      query,
      {
        body: JSON.stringify({
          query,
          variables,
        }),
        ...options,
      },
      variables,
    );
  }

  public async requestWithBlobs (
    query: string,
    variables: GQLVariables,
    blobs: (GQLBlob | Blob) | (GQLBlob[] | Blob[]),
    opts?: Options,
  ) {
    // comply to the graphql mulitpart request spec. see https://github.com/jaydenseric/graphql-multipart-request-spec#multipart-form-field-structure
    // -F operations='{ "query": "mutation ($file: Upload!) { singleUpload(file: $file) { id } }", "variables": { "file": null } }' \
    // -F map='{ "0": ["variables.file"] }' \
    // -F 0=@a.txt
    const form = new FormData();

    const operations = {
      query,
      variables,
    };

    const filesMap = {};
    const iterableBlobs = (!Array.isArray(blobs)) ? [blobs] : blobs;
    iterableBlobs.forEach(blob => {
      const file = ('file' in blob) ? blob.file : blob;
      const blobFieldName = ('name' in blob) ? blob.name : 'blob';
      filesMap[blobFieldName] = [`variables.${blobFieldName}`];
      operations.variables[blobFieldName] = null;
      form.append(blobFieldName, file);
    });
    form.append('operations', JSON.stringify(operations));
    form.append('map', JSON.stringify(filesMap));

    return this._request(
      query,
      variables,
      {
        body: form,
        ...opts,
      },
    );
  }

  public use (plugin: GQLPlugin, pluginOpts?: unknown) {
    const beforeRequest = {
      push: (...args) => this.beforeRequest.push(...args),
    };
    const afterResponse = {
      push: (...args) => this.afterResponse.push(...args),
    };
    const onClientError = {
      push: (...args) => this.onClientError.push(...args),
    };
    plugin(
      { beforeRequest, afterResponse, onClientError, GQLClient: this },
      pluginOpts,
    );
  }
}

interface GQLClient {
  cancelLast(cancelKey: string): void;
}

export default GQLClient;
